/**
 * Mobile Transaction Header Component
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Header with back button and title
 */

import { useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { Box, Paper, IconButton, Typography, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getTransactionFriendlyId, truncateIdForDisplay, copyToClipboard } from "../../../../utils/transactionUtils";
import CopyFeedbackTooltip from "../../../../components/CopyFeedbackTooltip";

export interface MobileTransactionHeaderProps {
  transactionType: "expense" | "income" | "transfer";
  isEditMode: boolean;
  workspaceSlug: string | undefined;
  addOptionDialogOpen: boolean;
  routeStorageKey: string;
  onBackClick: () => void;
  onClearNewlyAddedOptions: () => void;
  /** When provided, show transaction ID with copy in edit mode (top right). Not shown in create mode. */
  transactionIdPrefix?: string;
  friendlyIdSequence?: number;
  /** Next sequence for new transactions (create mode: not shown in header; backend still assigns ID). */
  nextFriendlyIdSequence?: number;
  transactionId?: string;
  /** Used for copy feedback key; ID is shown in edit mode regardless of member count. */
  workspaceMembersCount?: number;
  /** Override the main title (e.g. "Edit Fee" for fee edit). */
  titleOverride?: string;
  /** When provided, render this instead of the title (e.g. type tabs in create mode). */
  centerContent?: ReactNode;
}

export default function MobileTransactionHeader({
  transactionType,
  isEditMode,
  workspaceSlug,
  addOptionDialogOpen,
  routeStorageKey,
  onBackClick,
  onClearNewlyAddedOptions,
  transactionIdPrefix = "",
  friendlyIdSequence,
  nextFriendlyIdSequence,
  transactionId,
  workspaceMembersCount = 0,
  titleOverride,
  centerContent,
}: MobileTransactionHeaderProps) {
  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showCopiedFeedback = useCallback((key: string) => {
    if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    setLastCopiedKey(key);
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setLastCopiedKey(null);
      copyFeedbackTimeoutRef.current = null;
    }, 2000);
  }, []);
  useEffect(() => () => { if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current); }, []);

  // Single source of truth: the ID string we show and use for copy (edit = actual ID, create = next ID)
  const nextSeq = nextFriendlyIdSequence != null ? nextFriendlyIdSequence : 1;
  const prefix = (transactionIdPrefix ?? "").trim();
  const friendlyId = getTransactionFriendlyId(transactionIdPrefix, friendlyIdSequence);
  const editModeIdString = isEditMode && transactionId
    ? (friendlyId ?? transactionId)
    : undefined;
  const createModeIdString = !isEditMode && prefix
    ? getTransactionFriendlyId(prefix, nextSeq)
    : undefined;
  const idToShow = editModeIdString ?? createModeIdString ?? undefined;

  const showIdBlock = isEditMode && !!idToShow; // Edit: always show ID in top right when we have it; Create: hide ID so header is only back + type tabs
  const displayText = idToShow ?? (isEditMode ? "" : "—"); // Same value for display and copy; placeholder in create when no prefix
  const showCopyIcon = idToShow != null && idToShow.length > 0; // Copy only when there is a real ID to copy
  const handleBackClick = (e?: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Only prevent if add option dialog is open (user is in the middle of adding an option)
    if (addOptionDialogOpen) {
      return;
    }
    
    // Always allow back navigation - even if workspaceSlug is missing, try to navigate
    // Clear newly added options when navigating away without creating transaction
    onClearNewlyAddedOptions();
    
    // Call parent handler (parent will handle clearing route storage and navigation)
    onBackClick();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        p: 1.6,
        display: "flex",
        alignItems: "center",
        gap: 1,
        position: "sticky",
        top: 0,
        zIndex: 1500, // Higher z-index to ensure it's always on top and clickable
        bgcolor: "background.paper",
        pointerEvents: addOptionDialogOpen ? "none" : "auto",
        opacity: addOptionDialogOpen ? 0.5 : 1,
        transition: "opacity 0.2s ease",
      }}
      onMouseDown={(e) => {
        // Ensure header is always clickable - don't let events bubble down
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        // Ensure header is always clickable on mobile
        e.stopPropagation();
      }}
    >
      <IconButton 
        onClick={handleBackClick}
        onMouseDown={(e) => {
          // Stop propagation to ensure click works
          e.stopPropagation();
          e.preventDefault();
        }}
        onTouchStart={(e) => {
          // Stop propagation on mobile
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          // Ensure touch events work on mobile
          e.stopPropagation();
          if (!addOptionDialogOpen) {
            handleBackClick(e as any);
          }
        }}
        disabled={addOptionDialogOpen}
        sx={{
          position: "relative",
          zIndex: 1501, // Even higher z-index for the button itself 
          p: 0.75,
          color: "text.primary",
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          "&:disabled": {
            color: "action.disabled",
          }
        }}
      >
        <ArrowBackIcon sx={{ fontSize: "1.25rem" }} />
      </IconButton>
      {centerContent != null ? (
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", width: "100%", "& > *": { flex: 1, minWidth: 0, width: "100%" } }}>
          {centerContent}
        </Box>
      ) : (
        <Typography 
          variant="subtitle1" 
          sx={{ 
            flex: 1, 
            color: "text.primary",
            fontWeight: 500,
            fontSize: "1rem"
          }}
        >
          {titleOverride ?? (isEditMode 
            ? `Edit ${transactionType === "transfer" ? "Transfer" : transactionType === "income" ? "Income" : "Expense"}`
            : transactionType === "transfer" ? "Transfer" : transactionType === "income" ? "Income" : "Expense")}
        </Typography>
      )}
      {showIdBlock && (
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0, minWidth: 0 }} role="region" aria-label="Transaction ID">
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={idToShow ?? "Set in Workspace settings"}>
            {idToShow ? truncateIdForDisplay(idToShow) : displayText}
          </Typography>
          {showCopyIcon && idToShow && (
            <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={isEditMode ? `mobile-header-id-${transactionId}` : "mobile-header-id-new"} lastCopiedKey={lastCopiedKey} isMobile={true}>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(idToShow).then((ok) => ok && showCopiedFeedback(isEditMode ? `mobile-header-id-${transactionId}` : "mobile-header-id-new"))}
                sx={{ p: 0.25 }}
                aria-label="Copy transaction ID"
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </CopyFeedbackTooltip>
          )}
        </Stack>
      )}
    </Paper>
  );
}
